import { lazy } from "react";
import { createBrowserRouter } from "react-router-dom";
import { Layout } from "./components/Layout.tsx";
import { Landing } from "./pages/Landing.tsx";

// Every route except the landing page is code-split.
//
// The landing page is the acquisition path and stays eager: making the first
// paint wait on a second request would trade the thing that matters most for
// bytes nobody had to download anyway. Everything else is loaded when it is
// actually navigated to — a visitor reading the pricing page has no use for the
// assessment runner, the operator console or the Stripe client, and before this
// they downloaded all three.
//
// `Layout` wraps the outlet in a Suspense boundary, so there is one loading
// state rather than one per route.
const FreeDiagnostic = lazy(() =>
  import("./pages/FreeDiagnostic.tsx").then((m) => ({ default: m.FreeDiagnostic })));
const Pricing = lazy(() =>
  import("./pages/Pricing.tsx").then((m) => ({ default: m.Pricing })));
const Account = lazy(() =>
  import("./pages/Account.tsx").then((m) => ({ default: m.Account })));
const StartRun = lazy(() =>
  import("./pages/StartRun.tsx").then((m) => ({ default: m.StartRun })));
const PaidRunner = lazy(() =>
  import("./pages/PaidRunner.tsx").then((m) => ({ default: m.PaidRunner })));
const Composite = lazy(() =>
  import("./pages/Composite.tsx").then((m) => ({ default: m.Composite })));
const TeamCalibration = lazy(() =>
  import("./pages/TeamCalibration.tsx").then((m) => ({ default: m.TeamCalibration })));
const Participate = lazy(() =>
  import("./pages/Participate.tsx").then((m) => ({ default: m.Participate })));
const ConfirmOptin = lazy(() =>
  import("./pages/ConfirmOptin.tsx").then((m) => ({ default: m.ConfirmOptin })));
const Methode = lazy(() =>
  import("./pages/Methode.tsx").then((m) => ({ default: m.Methode })));
const Livrables = lazy(() =>
  import("./pages/Livrables.tsx").then((m) => ({ default: m.Livrables })));
const APropos = lazy(() =>
  import("./pages/APropos.tsx").then((m) => ({ default: m.APropos })));
const Contact = lazy(() =>
  import("./pages/Contact.tsx").then((m) => ({ default: m.Contact })));
const Verifier = lazy(() =>
  import("./pages/Verifier.tsx").then((m) => ({ default: m.Verifier })));
const Proofs = lazy(() =>
  import("./pages/Proofs.tsx").then((m) => ({ default: m.Proofs })));
const PLTest = lazy(() =>
  import("./pages/PLTest.tsx").then((m) => ({ default: m.PLTest })));
const Trajectory = lazy(() =>
  import("./pages/Trajectory.tsx").then((m) => ({ default: m.Trajectory })));
const Ops = lazy(() =>
  import("./pages/Ops.tsx").then((m) => ({ default: m.Ops })));
const SignIn = lazy(() =>
  import("./pages/SignIn.tsx").then((m) => ({ default: m.SignIn })));
const MentionsLegales = lazy(() =>
  import("./pages/Legal.tsx").then((m) => ({ default: m.MentionsLegales })));
const CGV = lazy(() =>
  import("./pages/Legal.tsx").then((m) => ({ default: m.CGV })));
const Confidentialite = lazy(() =>
  import("./pages/Legal.tsx").then((m) => ({ default: m.Confidentialite })));
const NotFound = lazy(() =>
  import("./pages/NotFound.tsx").then((m) => ({ default: m.NotFound })));

// Real paths, not hash routes: the marketing and pricing pages are the
// acquisition path and have to be crawlable, linkable and prerenderable.
export const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [
      { index: true, element: <Landing /> },
      { path: "diagnostic", element: <FreeDiagnostic /> },
      { path: "tarifs", element: <Pricing /> },
      { path: "merci", element: <Account /> },
      { path: "demarrer/:entId", element: <StartRun /> },
      { path: "run/:runId", element: <PaidRunner /> },
      { path: "composite", element: <Composite /> },
      // The falsification loop: evidence a claim, declare the result test,
      // and measure the direction a quarter later.
      { path: "preuves", element: <Proofs /> },
      { path: "resultat", element: <PLTest /> },
      { path: "trajectoire", element: <Trajectory /> },
      // Operator console. Deliberately absent from the navigation; the
      // database refuses it to anyone without the operator claim.
      { path: "ops", element: <Ops /> },
      { path: "equipe", element: <TeamCalibration /> },
      { path: "participer/:token", element: <Participate /> },
      { path: "confirmer/:token", element: <ConfirmOptin /> },
      { path: "methode", element: <Methode /> },
      { path: "livrables", element: <Livrables /> },
      { path: "a-propos", element: <APropos /> },
      { path: "contact", element: <Contact /> },
      // Two shapes: the address someone types, and the one printed on a
      // document so a link verifies on arrival.
      { path: "verifier", element: <Verifier /> },
      { path: "verifier/:digest", element: <Verifier /> },
      { path: "compte", element: <Account /> },
      { path: "connexion", element: <SignIn /> },
      { path: "mentions-legales", element: <MentionsLegales /> },
      { path: "cgv", element: <CGV /> },
      { path: "confidentialite", element: <Confidentialite /> },
      { path: "*", element: <NotFound /> },
    ],
  },
]);
